import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { GripVertical, CheckCircle2, Circle, Trash2 } from 'lucide-react';

const PipelineClientCard = ({ 
  client, 
  stage, 
  onDragStart, 
  onTaskToggle,
  onDeleteClick
}) => {
  const navigate = useNavigate();
  const stageTasks = stage.tasks || [];
  const completedTasks = client.completedStageTasks?.[stage.id] || [];
  const progress = stageTasks.length > 0 ? (completedTasks.length / stageTasks.length) * 100 : 0;

  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, client, stage.id)}
      className="bg-white border rounded-lg p-3 cursor-grab active:cursor-grabbing hover:shadow-md transition-shadow"
      data-testid={`client-card-${client.assignmentId}`}
    >
      <div className="flex items-start gap-2 mb-2">
        <GripVertical className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <h4 
              className="font-medium text-sm text-gray-900 truncate hover:text-blue-600 cursor-pointer"
              onClick={() => navigate(`/clients/${client._id}`)}
            >
              {client.displayName}
            </h4>
            {client.forSpouse && (
              <Badge className="bg-pink-100 text-pink-800 text-[10px] px-1.5 py-0">Spouse</Badge>
            )}
          </div>
          <p className="text-xs text-gray-500 truncate">{client.contactPerson}</p>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 text-gray-400 hover:text-red-500"
          onClick={(e) => {
            e.stopPropagation();
            onDeleteClick(client);
          }}
          data-testid={`delete-client-${client.assignmentId}`}
        >
          <Trash2 className="h-3 w-3" />
        </Button>
      </div>

      {/* Progress bar */}
      {stageTasks.length > 0 && (
        <div className="mb-2">
          <div className="flex items-center justify-between text-xs mb-1">
            <span className="text-gray-500">Progress</span>
            <span className="font-medium">{completedTasks.length}/{stageTasks.length}</span>
          </div>
          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div 
              className="h-full bg-green-500 transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      )}

      {/* Task checklist */}
      {stageTasks.length > 0 && (
        <div className="space-y-1">
          {stageTasks.map(task => {
            const isCompleted = completedTasks.includes(task.id);
            return (
              <div 
                key={task.id}
                className="flex items-center gap-2 text-xs cursor-pointer hover:bg-gray-50 rounded px-1 py-0.5"
                onClick={(e) => {
                  e.stopPropagation();
                  onTaskToggle(client, stage.id, task.id, isCompleted);
                }}
              >
                {isCompleted ? (
                  <CheckCircle2 className="h-3.5 w-3.5 text-green-500 flex-shrink-0" />
                ) : (
                  <Circle className="h-3.5 w-3.5 text-gray-300 flex-shrink-0" />
                )}
                <span className={isCompleted ? 'text-gray-400 line-through' : 'text-gray-700'}>
                  {task.name}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* Assignee */}
      {client.assignedTo && (
        <div className="mt-2 pt-2 border-t">
          <span className="text-xs text-gray-500">Assigned to: {client.assignedTo}</span>
        </div>
      )}
    </div>
  );
};

export default PipelineClientCard;
